/**
 * Demo placement-mode click capture hook.
 *
 * Listens for map clicks while `placementMode` is on and pushes new waypoints
 * into `dockDemoStore`. The actual route RENDERING (waypoint markers, line,
 * home legs) lives in `DockMissionRouteLayer` mounted at EditorPage level
 * so the route stays visible regardless of whether the dock-mission ticket
 * is open. This hook only needs to live as long as the panel is open —
 * placement mode is a panel-scoped UI affordance.
 */

import { useEffect } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';

export function useDemoWaypointPlacement(): void {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const placementMode = useDockDemoStore((s) => s.placementMode);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const missions = useDockDemoStore((s) => s.missions);
  const addWaypoint = useDockDemoStore((s) => s.addWaypoint);

  const mission = missions.find((m) => m.id === selectedMissionId);

  useEffect(() => {
    if (!map || !isMapReady || !placementMode || !mission) return;

    const handler = (e: MapMouseEvent) => {
      addWaypoint(mission.id, e.lngLat.lng, e.lngLat.lat);
    };

    const canvas = map.getCanvas();
    const previousCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';
    map.on('click', handler);

    return () => {
      map.off('click', handler);
      canvas.style.cursor = previousCursor;
    };
  }, [map, isMapReady, placementMode, mission, addWaypoint]);
}
